import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { Product, ProductMapValue } from '../../../../modals/product.modals';

@Component({
  selector: 'app-variant-selector',
  standalone: true,
  imports: [CommonModule, FormsModule, DialogModule, ButtonModule, InputTextModule],
  templateUrl: './variant-selector.component.html',
  styleUrls: ['./variant-selector.component.scss']
})
export class VariantSelectorComponent implements OnInit, OnChanges {
  @Input() product!: Product;
  @Output() variantSelected = new EventEmitter<ProductMapValue>();

  // Bump this from the parent (e.g. on a sitemap icon click) to pop the
  // dialog open directly, without the user needing to click a trigger
  // button/chip inside this component first.
  @Input() openSignal = 0;
  private lastOpenSignal = 0;

  // Fired once the dialog has opened from openSignal, so the parent can
  // clear the signal. Otherwise a re-created instance (the row's trackBy key
  // changes when the variant/mapping changes) would see the stale tick as
  // new and pop the dialog open again.
  @Output() openSignalConsumed = new EventEmitter<void>();

  // Controls the centered p-dialog (replaces the old p-overlayPanel ViewChild)
  displayDialog: boolean = false;

  filteredOptions: ProductMapValue[] = [];
  searchQuery: string = '';
  options: ProductMapValue[] = [];

  isSerialized: boolean = false;
  showVariantControl: boolean = false; // Renamed from hasMultipleChoices - now reflects data shape, not stock count
  needsAttention: boolean = false; // Controls highlighting

  ngOnInit() {
    this.initOptions();
  }

  ngOnChanges(changes: SimpleChanges) {
    this.initOptions();

    // React to the parent bumping openSignal (sitemap icon click) by
    // opening the dialog directly. Since this component stays mounted for
    // every row (so selected chips keep showing), the parent resets this
    // row's binding back to 0 whenever a *different* row is clicked - we
    // only open on a genuine new positive tick, never on that reset.
    if (changes['openSignal']) {
      const signal = this.openSignal;
      if (signal > 0 && signal !== this.lastOpenSignal) {
        this.openDialogPanel();
        this.openSignalConsumed.emit();
      }
      this.lastOpenSignal = signal;
    }
  }

  initOptions() {
    if (!this.product?.productmapvalues || this.product.productmapvalues.length === 0) {
      if (this.product?.selectedMapValue) {
        this.options = [this.product.selectedMapValue];
      } else {
        this.options = [];
        this.showVariantControl = false;
        return;
      }
    } else {
      this.options = this.product.productmapvalues;
    }
    this.filteredOptions = [...this.options];

    // Check if it's serialized (has IMEI/parameter1)
    this.isSerialized = this.options.some(opt => !!opt.parameter1_value);

    const hasParameter = this.options.some(opt => !!opt.parameter1_value || !!opt.parameter2_value);
    const hasVariant = this.options.some(opt => !!opt.variant1_value);
    const multipleUnits = this.options.length > 1;

    // Show the interactive selector when:
    //  - a parameter (IMEI) and a variant (e.g. color) are both present, or
    //  - only a variant is present (no parameter), or
    //  - only a parameter is present but there's more than one unit to pick between
    //    (kept so multi-serial products with no color option still stay selectable)
    this.showVariantControl = hasVariant || (hasParameter && multipleUnits);

    // Trigger visual pulse if choice required
    if (this.showVariantControl && !this.product.selectedMapValue) {
      this.needsAttention = true;
    } else {
      this.needsAttention = false;
    }
  }

  // Determines headers needed based on available parameters and variants
  // This helps make the dropdown list headers dynamic
  get optionHeaders() {
    return {
      hasParameter1: this.options.some(opt => !!opt.parameter1_value),
      hasParameter2: this.options.some(opt => !!opt.parameter2_value),
      hasVariant1: this.options.some(opt => !!opt.variant1_value),
      hasBatch: this.options.some(opt => !!opt.batch && opt.batch !== '')
    };
  }

  // Get dynamic names if we have them from the first option's parameters/variants array
  // (Assuming backend passes a structured `parameters` and `variants` array as per the payload schema)
  getOptionNames() {
    const firstOptWithParams = this.options.find(opt => (opt as any).parameters && (opt as any).parameters.length > 0);
    const firstOptWithVariants = this.options.find(opt => (opt as any).variants && (opt as any).variants.length > 0);

    const parameter1Name = firstOptWithParams ? (firstOptWithParams as any).parameters.find((p: any) => p.parameter_id === 1)?.parameter_name : 'Parameter 1';
    const parameter2Name = firstOptWithParams ? (firstOptWithParams as any).parameters.find((p: any) => p.parameter_id === 4)?.parameter_name : 'Parameter 2';
    const variant1Name = firstOptWithVariants ? (firstOptWithVariants as any).variants.find((v: any) => v.variant_id === 1)?.variant_name : 'Variant 1';

    return {
      parameter1Name: parameter1Name || 'IMEI/Serial',
      parameter2Name: parameter2Name || 'Serial No',
      variant1Name: variant1Name || 'Color'
    };
  }

  // How many columns the table currently renders (parameter1/parameter2/
  // variant1 are all conditional, qty is always shown) - used for the
  // "No variants found" row's colspan.
  get colSpanCount(): number {
    const headers = this.optionHeaders;
    let count = 1; // Qty column
    if (headers.hasParameter1) count++;
    if (headers.hasParameter2) count++;
    if (headers.hasVariant1) count++;
    return count;
  }

  togglePanel(event?: MouseEvent) {
    this.openDialogPanel();
  }

  // Shared open logic used both by the internal chip click and by the
  // parent's sitemap icon (via openSignal).
  openDialogPanel() {
    if (!this.showVariantControl && !this.product.selectedMapValue) return;
    this.needsAttention = false; // Stop pulsating when opened
    this.displayDialog = true;
    this.searchQuery = '';
    this.filteredOptions = [...this.options];
  }

  closeDialog() {
    this.displayDialog = false;
  }

  onSearch(event: any) {
    const query = event.target.value?.toLowerCase() || '';
    if (!query) {
      this.filteredOptions = [...this.options];
      return;
    }

    this.filteredOptions = this.options.filter(opt => {
      const param1 = (opt.parameter1_value || '').toLowerCase();
      const param2 = (opt.parameter2_value || '').toLowerCase();
      const variant1 = (opt.variant1_value || '').toLowerCase();
      return param1.includes(query) || param2.includes(query) || variant1.includes(query);
    });

    // Check for exact IMEI match on in-stock items
    if (this.isSerialized) {
      const exactMatch = this.options.find(opt =>
        ((opt.parameter1_value || '').toLowerCase() === query ||
          (opt.parameter2_value || '').toLowerCase() === query) &&
        opt.qty > 0
      );

      if (exactMatch) {
        this.selectVariant(exactMatch);
      }
    }
  }

  selectVariant(option: ProductMapValue) {
    if (option.qty <= 0) return;
    // The parent assigns product.selectedMapValue after validating stock
    // across the cart, so a rejected pick never leaves the row mutated.
    this.variantSelected.emit(option);
    this.displayDialog = false;
    this.searchQuery = '';
    this.filteredOptions = [...this.options];
  }

  getButtonLabel(): string {
    if (this.product.selectedMapValue) {
      return this.getChipText();
    }

    if (!this.showVariantControl && this.options.length === 1) {
      return this.formatOptionText(this.options[0]);
    }

    return 'Select Variant';
  }

  // Serialized items show IMEI + Color; non-serialized (variant-only) items
  // show Color + Qty instead, since there's no IMEI to identify the unit by.
  getChipText(): string {
    const val = this.product.selectedMapValue;
    if (!val) return 'Select';

    const hasParam1 = !!val.parameter1_value;
    const colorText = val.variant1_value ? String(val.variant1_value) : '';

    if (hasParam1) {
      // Truncate IMEI / Serial or long identifiers
      const str = val.parameter1_value as string;
      // const imeiText = str.length > 5 ? `···${str.slice(-4)}` : str;
      return colorText ? `${val.parameters?.find(p => p.parameter_id === 1)?.parameter_name}: ${str}  ${val.variants?.find(v => v.variant_id === 1)?.variant_name}: ${colorText}` : str;
    }

    const qtyText = val.qty !== undefined ? `Qty: ${val.qty}` : '';
    if (colorText && qtyText) return `${colorText}`;
    return colorText || qtyText || 'Selected';
  }

  getParameter1Name(): string {
    const val = this.product.selectedMapValue;
    if (!val) return 'IMEI';
    return val.parameters?.[0]?.parameter_name || val.parameters?.find(p => p.parameter_id === 1)?.parameter_name || 'IMEI';
  }

  getVariant1Name(): string {
    const val = this.product.selectedMapValue;
    if (!val) return 'Color';
    return val.variants?.[0]?.variant_name || val.variants?.find(v => v.variant_id === 1)?.variant_name || 'Color';
  }

  formatOptionText(option: ProductMapValue): string {
    let text = '';
    if (option.parameter1_value) {
      text += option.parameter1_value;
    }
    // if (option.parameter2_value) {
    //   text += text ? ` | ${option.parameter2_value}` : option.parameter2_value;
    // }
    if (option.variant1_value) {
      text += text ? ` - ${option.variant1_value}` : option.variant1_value;
    }
    return text;
  }

  getFullVariantDetails(): string {
    const val = this.product.selectedMapValue;
    if (!val) return '';

    let details: string[] = [];

    if (val.parameter1_value) {
      const param1Name = this.getOptionNames().parameter1Name;
      details.push(`${param1Name}: ${val.parameter1_value}`);
    }

    if (val.parameter2_value) {
      const param2Name = this.getOptionNames().parameter2Name;
      details.push(`${param2Name}: ${val.parameter2_value}`);
    }

    if (val.variant1_value) {
      const variantName = this.getOptionNames().variant1Name;
      details.push(`${variantName}: ${val.variant1_value}`);
    }

    if (val.qty !== undefined) {
      details.push(`Available Qty: ${val.qty}`);
    }

    return details.join(' | ');
  }

  getColorDot(colorName?: string | any): string {
    if (!colorName) return '#ccc';

    const colorMap: { [key: string]: string } = {
      'red': '#ff4d4d',
      'blue': '#2A5EDE',
      'green': '#22c55e',
      'yellow': '#eab308',
      'black': '#1f2937',
      'white': '#f9fafb',
      'silver': '#9ca3af',
      'gold': '#fbbf24',
      'grey': '#6b7280',
      'gray': '#6b7280',
      'purple': '#a855f7',
      'pink': '#ec4899',
      'orange': '#f97316'
    };

    const normalized = colorName.toLowerCase();
    for (const key in colorMap) {
      if (normalized.includes(key)) return colorMap[key];
    }

    return '#ccc'; // Default
  }
}
